import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import down_chevron from "app/images/down-chevron.svg";
import { cls } from "app/utils";
import styles from "./styles.module.scss";

// See WizardTextField: no useId in this React version, so a module counter backs
// the label/control association.
let instanceCount = 0;

interface IOption {
  value: string;
  label: string;
}

interface IProps {
  label?: string;
  value?: string;
  placeholder?: string;
  options?: IOption[];
  error?: string;
  /** Announced to assistive tech; the form is noValidate, so validation stays the step's. */
  required?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
}

// Space between the field and the floating list.
const DROPDOWN_GAP = 4;

// How long a pause resets the type-ahead buffer ("ne" -> Nebraska, wait, "o" ->
// Ohio rather than continuing to "neo").
const TYPE_AHEAD_RESET_MS = 500;

// A custom dropdown rather than a native <select> (or DropdownButton, whose
// list is position: absolute): AcademyWizardLayout's card_body is
// overflow-y: auto, and an absolutely positioned open list still counts toward
// a scroll container's scrollable area, stretching the card — the same trap
// PhoneField documents. The list is therefore position: fixed and anchored to
// the field by measurement, flipping above it when the viewport runs out.
const WizardSelectField = ({
  label,
  value,
  placeholder,
  options,
  error,
  required,
  onChange,
  onBlur,
}: IProps) => {
  const [id] = useState(() => `wizard-select-field-${(instanceCount += 1)}`);
  const errorId = error ? `${id}-error` : undefined;
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typeAhead = useRef({ buffer: "", lastKeyAt: 0 });

  const selectedIndex = options
    ? options.findIndex((option) => option.value === value)
    : -1;
  const selected = selectedIndex >= 0 ? options?.[selectedIndex] : undefined;

  const close = useCallback(() => {
    setIsOpen(false);
    if (onBlur) {
      onBlur();
    }
  }, [onBlur]);

  const open = () => {
    setHighlighted(selectedIndex >= 0 ? selectedIndex : 0);
    setIsOpen(true);
  };

  const pick = (option: IOption) => {
    if (onChange) {
      onChange(option.value);
    }
    close();
    buttonRef.current?.focus();
  };

  // Anchors the fixed list to the field. Runs after render (the list must exist
  // to measure its height for the flip decision), then again on any scroll or
  // resize — a fixed box does not follow ancestor scrolling, so re-anchoring
  // keeps it glued to the field while the wizard card scrolls under it.
  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const anchor = () => {
      const field = buttonRef.current;
      const list = listRef.current;
      if (!field || !list) {
        return;
      }
      const rect = field.getBoundingClientRect();
      const openUp =
        rect.bottom + DROPDOWN_GAP + list.offsetHeight > window.innerHeight;

      list.style.width = `${rect.width}px`;
      list.style.left = `${rect.left}px`;
      list.style.top = openUp
        ? `${rect.top - DROPDOWN_GAP - list.offsetHeight}px`
        : `${rect.bottom + DROPDOWN_GAP}px`;
    };

    anchor();
    // Reveal the current choice without animating past the whole list.
    listRef.current
      ?.querySelector('[aria-selected="true"]')
      ?.scrollIntoView({ block: "nearest" });

    // Capture phase, since scroll does not bubble from the card.
    window.addEventListener("scroll", anchor, true);
    window.addEventListener("resize", anchor);
    return () => {
      window.removeEventListener("scroll", anchor, true);
      window.removeEventListener("resize", anchor);
    };
  }, [isOpen]);

  // Outside interactions close the list. mousedown rather than click so the
  // list is gone before the outside control processes its own press.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !listRef.current?.contains(target)
      ) {
        close();
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
    };
  }, [isOpen, close]);

  useEffect(() => {
    if (isOpen) {
      const items = listRef.current?.querySelectorAll(`.${styles.option}`);
      items?.[highlighted]?.scrollIntoView({ block: "nearest" });
    }
  }, [isOpen, highlighted]);

  const moveHighlight = (to: number) => {
    if (!options?.length) {
      return;
    }
    setHighlighted(Math.max(0, Math.min(options.length - 1, to)));
  };

  // "ca" jumps to California: printable keys accumulate briefly and match the
  // first option label starting with the buffer, like a native <select>.
  const onTypeAhead = (key: string) => {
    if (!options?.length) {
      return;
    }
    const now = Date.now();
    const { buffer, lastKeyAt } = typeAhead.current;
    const next =
      now - lastKeyAt > TYPE_AHEAD_RESET_MS
        ? key.toLowerCase()
        : buffer + key.toLowerCase();
    typeAhead.current = { buffer: next, lastKeyAt: now };

    const match = options.findIndex((option) =>
      option.label.toLowerCase().startsWith(next),
    );
    if (match >= 0) {
      if (isOpen) {
        setHighlighted(match);
      } else if (onChange) {
        onChange(options[match].value);
      }
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!options?.length) {
      return;
    }
    if (!isOpen) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        open();
      } else if (event.key.length === 1 && event.key !== " ") {
        onTypeAhead(event.key);
      }
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveHighlight(highlighted + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        moveHighlight(highlighted - 1);
        break;
      case "Home":
        event.preventDefault();
        moveHighlight(0);
        break;
      case "End":
        event.preventDefault();
        moveHighlight(options.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        pick(options[highlighted]);
        break;
      case "Escape":
      case "Tab":
        close();
        break;
      default:
        if (event.key.length === 1) {
          onTypeAhead(event.key);
        }
    }
  };

  if (options && onChange) {
    return (
      <div className={styles.container} ref={containerRef}>
        {label && (
          <label className={styles.label} htmlFor={id}>
            {label}
          </label>
        )}
        <button
          type="button"
          id={id}
          ref={buttonRef}
          className={cls(styles.field, {
            [styles.field_error]: !!error,
            [styles.field_open]: isOpen,
          })}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-invalid={!!error}
          aria-required={required || undefined}
          aria-describedby={errorId}
          // Focus stays on the button while arrows move the highlight, so the
          // active option must be exposed via activedescendant. Both ids only
          // resolve while the list is mounted, hence the isOpen guard.
          aria-controls={isOpen ? `${id}-listbox` : undefined}
          aria-activedescendant={
            isOpen ? `${id}-option-${highlighted}` : undefined
          }
          onClick={() => (isOpen ? close() : open())}
          onKeyDown={onKeyDown}
          // Tabbing away from the closed field still counts as touching it;
          // while the list is open, close() owns the onBlur call instead.
          onBlur={() => {
            if (!isOpen && onBlur) {
              onBlur();
            }
          }}
        >
          {selected ? (
            <span className={styles.value}>{selected.label}</span>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
          <img
            src={down_chevron}
            alt=""
            className={cls(styles.chevron, { [styles.chevron_open]: isOpen })}
          />
        </button>
        {isOpen && (
          <ul
            id={`${id}-listbox`}
            className={styles.list}
            role="listbox"
            ref={listRef}
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                id={`${id}-option-${index}`}
                role="option"
                aria-selected={option.value === value}
                className={cls(styles.option, {
                  [styles.option_highlighted]: index === highlighted,
                })}
                // mousedown, so the pick lands before the outside-click handler
                // and before the button's blur can interfere.
                onMouseDown={(event) => {
                  event.preventDefault();
                  pick(option);
                }}
                onMouseEnter={() => setHighlighted(index)}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
        {error && (
          <span id={errorId} className={styles.error}>
            {error}
          </span>
        )}
      </div>
    );
  }

  // Static presentational select: design-only steps have no data to populate
  // and no change handler, so this renders a value, not a control.
  return (
    <div className={styles.container}>
      {label && <span className={styles.label}>{label}</span>}
      <div className={styles.input}>
        {value ? (
          <span className={styles.value}>{value}</span>
        ) : (
          <span className={styles.placeholder}>{placeholder}</span>
        )}
        <img src={down_chevron} alt="" className={styles.chevron} />
      </div>
    </div>
  );
};

export default memo(WizardSelectField);
